<template>
  <div class="login-page">
    <!-- 粒子背景容器 -->
    <div id="particles-js" class="particles-container"></div>
    
    <!-- 悬浮元素 -->
    <div class="floating-elements">
      <div v-for="(circle, index) in floatCircles" :key="index"
           :class="`float-circle circle-${index}`"
           :style="{
             width: circle.size + 'px',
             height: circle.size + 'px',
             left: circle.left + '%',
             top: circle.top + '%',
             backgroundColor: circle.bgColor
           }">
      </div>
    </div>
    
    <!-- 登录卡片 -->
    <div class="login-container" :class="{ 'card-shake': shakeCard }">
      <div class="login-card">
        <!-- 登录头 -->
        <div class="login-header">
          <div class="logo" :class="{ 'logo-pulse': pulseEffect }">
            <img src="@/assets/home.jpg">
          </div>
          <h1>安全登录</h1>
          <p>领班人广告连锁系统</p>
        </div>
        
        <!-- 登录表单 -->
        <el-form 
          :model="loginForm" 
          :rules="loginRules" 
          ref="loginFormRef"
          @submit.native.prevent="handleLogin">
          
          <!-- 用户名 -->
          <el-form-item prop="username">
            <div class="input-group">
              <i class="icon-user"></i>
              <el-input 
                v-model="loginForm.username" 
                class="input-field"
                placeholder="用户名/邮箱"
                autocomplete="off">
              </el-input>
            </div>
          </el-form-item>
          
          <!-- 密码 -->
          <el-form-item prop="password" class="password-item">
            <div class="input-group">
              <i class="icon-lock"></i>
              <el-input 
                v-model="loginForm.password" 
                type="password"
                class="input-field"
                placeholder="密码"
                show-password>
              </el-input>
            </div>
          </el-form-item>
          
          <!-- 验证码 -->
          <el-form-item prop="captcha" class="captcha-item">
            <div class="captcha-container">
              <div class="input-group">
                <i class="icon-shield"></i>
                <el-input 
                  v-model="loginForm.captcha" 
                  class="input-field captcha-input"
                  placeholder="请输入验证码"
                  maxlength="4">
                </el-input>
              </div>
              <div class="captcha-image-container" @click="refreshCaptcha" style="margin-bottom:28px">
                <img class="captcha-image" :src="captchaImage">
                <i class="icon-refresh refresh-icon"></i>
              </div>
            </div>
          </el-form-item>
          
          <!-- 登录按钮 -->
          <el-form-item>
            <el-button 
              native-type="submit"
              :loading="loading"
              class="login-btn">
              <span v-if="!loading">登录系统</span>
              <span v-else>登录中...</span>
            </el-button>
          </el-form-item>
        </el-form>
        
        <!-- 注册链接 -->
        <div class="register-link">
          <span>没有账户?</span>
          <el-link type="primary" @click="showRegisterModal">立即注册</el-link>
        </div>
      </div>
    </div>
    
    <!-- 登录成功提示 -->
    <transition name="zoom-fade">
      <div v-if="loginSuccess" class="login-success-overlay">
        <div class="success-icon">
          <i class="icon-check"></i>
        </div>
        <h2>欢迎回来，管理员！</h2>
        <p>正在进入系统...</p>
      </div>
    </transition>



    <div class="footer">
      <div class="footer-content">
        <p>Copyright © 2025 领班人广告连锁系统</p>
        <p>
          <a href="https://beian.miit.gov.cn/" target="_blank">ICP备案号: 蜀ICP备2024109498号-1</a>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { login, captcha } from '../api/admin'

export default {
  name: 'LoginPage',
  data() {
    // 验证码验证规则
    const validateCaptcha = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入验证码'));
      } else if (value.length !== 4) {
        callback(new Error('验证码长度必须为4位'));
      } else {
        callback();
      }
    };
    
    return {
      pulseEffect: true,
      shakeCard: false,
      loading: false,
      loginSuccess: false,
      loginForm: {
        username: '',
        password: '',
        captcha: '' // 新增验证码字段
      },
      loginRules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, message: '密码长度至少为6位', trigger: 'blur' }
        ],
        captcha: [
          { required: true, validator: validateCaptcha, trigger: 'blur' }
        ]
      },
      captchaImage: '', // 验证码图片URL
      captchaId:'',
      floatCircles: [
        { size: 300, left: -10, top: 10, bgColor: 'rgba(86, 58, 253, 0.4)' },
        { size: 200, left: 85, top: 60, bgColor: 'rgba(25, 119, 243, 0.3)' },
        { size: 150, left: 95, top: 15, bgColor: 'rgba(86, 58, 253, 0.3)' }
      ]
    };
  },
  mounted() {
    // 初始化粒子背景
    this.initParticles();
    // 启动悬浮元素动画
    this.animateFloatingElements();
    // 初始化验证码
    this.refreshCaptcha();
  },
  methods: {
    initParticles() {
      const particlesConfig = {
        particles: {
          number: { value: 80, density: { enable: true, value_area: 800 } },
          color: { value: "#ffffff" },
          shape: { type: "circle" },
          opacity: { value: 0.5, random: true },
          size: { value: 3, random: true },
          line_linked: {
            enable: true,
            distance: 150,
            color: "#ffffff",
            opacity: 0.3,
            width: 1
          },
          move: {
            enable: true,
            speed: 2,
            direction: 'none',
            random: true,
            straight: false,
            out_mode: 'out',
            bounce: false
          }
        },
        interactivity: {
          detect_on: 'canvas',
          events: {
            onhover: { enable: true, mode: 'repulse' },
            onclick: { enable: true, mode: 'push' },
            resize: true
          }
        }
      };
      
      // 调用粒子初始化函数（particlesJS函数需要在本地可用）
      if (typeof window.particlesJS === 'function') {
        window.particlesJS('particles-js', particlesConfig);
      }
    },
    animateFloatingElements() {
      const circles = document.querySelectorAll('.float-circle');
      circles.forEach((circle, index) => {
        // 使用CSS变量驱动动画
        circle.style.setProperty('--delay', `${index}s`);
        circle.style.setProperty('--direction', index % 2 ? 1 : -1);
      });
    },
    // 刷新验证码
    refreshCaptcha() {
      captcha().then(res => {
        this.captchaImage = res.data.image
        this.captchaId = res.data.captcha_id
      }).catch(error => {
        this.$message.error('验证码获取失败')
        console.error('验证码获取失败:', error)
      })

    },
    handleLogin() {
      this.$refs.loginFormRef.validate(valid => {
        if (!valid) {
          // 表单验证失败时添加抖动效果
          this.shakeCard = true;
          setTimeout(() => this.shakeCard = false, 500);
          return;
        }
        // 开始登录
        this.loading = true;
        login({
          username: this.loginForm.username,
          password: this.loginForm.password,
          captcha_id: this.captchaId,
          captcha: this.loginForm.captcha
        }).then(res => {
          if (res.code == 1) {
            localStorage.setItem('token', res.data.token)
            localStorage.setItem('username', res.data.username)
            this.$router.push('/home')
            this.$message.success('登录成功')
          }
        }).catch(err  => {
          this.refreshCaptcha()
          this.$message.error(err.error)
        }).finally(() => {
          this.loading = false
        })

      });
    },
    showPasswordHint() {
      this.$message({
        message: '请联系管理员重置密码',
        type: 'info',
        duration: 3000,
        customClass: 'password-hint-message'
      });
    },
    showRegisterModal() {
      this.$alert('系统注册功能暂未开放，请联系管理员进行账号注册', '注册提示', {
        confirmButtonText: '确定',
        customClass: 'register-alert'
      });
    }
  },
  beforeDestroy() {
    // 清理粒子效果
    if (window.pJSDom && window.pJSDom.length > 0) {
      window.pJSDom[0].pJS.fn.vendors.destroy();
    }
  }
};
</script>


<style>
/* 基础样式 */
.login-page {
  position: relative;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0c29, #302b63, #24243e);
  background-size: 400% 400%;
  animation: gradientBG 25s ease infinite;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

/* 粒子背景容器 */
.particles-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

/* 背景动画 */
@keyframes gradientBG {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

/* 登录容器 */
.login-container {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 450px;
  padding: 0 20px;
  transition: transform 0.3s ease;
}

/* 抖动效果 */
.card-shake {
  animation: shake 0.5s ease;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
}

/* 登录卡片 */
.login-card {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(12px);
  border-radius: 20px;
  padding: 40px 30px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
}

.login-card::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: conic-gradient(
    transparent, rgba(168, 239, 255, 0.1), transparent 30%
  );
  animation: rotate 6s linear infinite;
}

@keyframes rotate {
  100% { transform: rotate(360deg); }
}

/* 登录头 */
.login-header {
  text-align: center;
  margin-bottom: 35px;
  position: relative;
  z-index: 2;
}

/* 修改后的logo样式 */
.logo {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin: 0 auto 20px;
  box-shadow: 0 0 25px rgba(0, 114, 255, 0.7);
  overflow: hidden; /* 确保图片不会超出圆形边界 */
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1); /* 添加一个备用背景 */
}

.logo img {
  width: 100%;
  height: 100%;
  object-fit: cover; /* 保持图片比例并填满容器 */
}

/* 脉冲动画 */
.logo-pulse {
  animation: pulse 4s ease-in-out infinite;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(0, 114, 255, 0.7); }
  70% { box-shadow: 0 0 0 25px rgba(0, 114, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 114, 255, 0); }
}

.login-header h1 {
  font-weight: 600;
  color: white;
  font-size: 28px;
  letter-spacing: 1px;
  margin-bottom: 5px;
  text-transform: uppercase;
}

.login-header p {
  color: rgba(255, 255, 255, 0.7);
  font-weight: 300;
  font-size: 16px;
}

/* 输入框 */
.input-group {
  position: relative;
  margin-bottom: 25px;
  transition: transform 0.3s;
}

.input-group:hover {
  transform: translateY(-3px);
}

.input-field .el-input__inner {
  width: 100%;
  background: rgba(255, 255, 255, 0.1) !important;
  border: none !important;
  outline: none;
  padding: 15px 20px 15px 50px;
  border-radius: 50px !important;
  color: white !important;
  font-size: 16px;
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);
}

.input-field .el-input__inner:focus {
  background: rgba(255, 255, 255, 0.15) !important;
  box-shadow: 0 0 20px rgba(0, 200, 255, 0.3);
}

/* 输入图标 */
.input-group i {
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.6);
  transition: all 0.3s ease;
  z-index: 10;
  font-size: 18px;
}

.input-field .el-input__inner:focus + i {
  color: #00c6ff !important;
}

/* 忘记密码 */
.forgot-password {
  display: block;
  text-align: right;
  color: rgba(255, 255, 255, 0.6) !important;
  text-decoration: none;
  font-size: 14px !important;
  margin-top: -15px;
  margin-bottom: 20px;
  transition: all 0.3s ease;
}

.forgot-password:hover {
  color: #00c6ff !important;
  text-shadow: 0 0 10px rgba(0, 200, 255, 0.8);
}

/* 登录按钮 */
.login-btn.el-button {
  background: linear-gradient(to right, #00c6ff, #0072ff);
  border: none;
  outline: none;
  color: white;
  padding: 15px;
  border-radius: 50px;
  width: 100%;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 20px rgba(0, 114, 255, 0.3);
  letter-spacing: 1px;
  text-transform: uppercase;
}

.login-btn.el-button span {
  position: relative;
  z-index: 1;
}

.login-btn.el-button:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(0, 114, 255, 0.5);
}

.login-btn.el-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  transition: 0.5s;
}

.login-btn.el-button:hover::before {
  left: 100%;
}

/* 社交登录 */
.social-login {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 30px;
}

.social-btn.el-button {
  width: 50px;
  height: 50px;
  padding: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 20px;
  transition: all 0.3s ease;
  border: none;
  position: relative;
  overflow: hidden;
}

.social-btn.el-button:hover {
  transform: translateY(-5px) scale(1.1);
}

.social-btn-0 {
  background: linear-gradient(to right, #4568dc, #b06ab3) !important;
  box-shadow: 0 5px 15px rgba(69, 104, 220, 0.4);
}

.social-btn-1 {
  background: linear-gradient(to right, #3b5998, #8b9dc3) !important;
  box-shadow: 0 5px 15px rgba(59, 89, 152, 0.4);
}

.social-btn-2 {
  background: linear-gradient(to right, #4285f4, #34a853, #fbbc05, #ea4335) !important;
  box-shadow: 0 5px 15px rgba(234, 67, 53, 0.4);
}

/* 注册链接 */
.register-link {
  text-align: center;
  margin-top: 30px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
}

.register-link .el-link {
  font-weight: 600;
  margin-left: 5px;
}

/* 悬浮元素 */
.floating-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}

.float-circle {
  position: absolute;
  border-radius: 50%;
  animation: float 12s infinite ease-in-out;
  animation-delay: var(--delay);
  transform: translateY(0px) rotate(0deg);
  animation-direction: alternate;
}

@keyframes float {
  0% { 
    transform: translateY(0) rotate(0deg) scale(1);
  }
  50% { 
    transform: translateY(-40px) rotate(calc(180deg * var(--direction))) scale(1.05);
  }
  100% { 
    transform: translateY(0) rotate(calc(360deg * var(--direction))) scale(1);
  }
}

/* 登录成功覆盖层 */
.login-success-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 123, 255, 0.9);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 2rem;
  z-index: 2000;
  text-align: center;
}

.success-icon {
  font-size: 5rem;
  margin-bottom: 20px;
  animation: zoomIn 0.8s ease;
}

.login-success-overlay h2 {
  margin-bottom: 10px;
}

.login-success-overlay p {
  font-size: 1rem;
  opacity: 0.9;
}

/* 过渡动画 */
.zoom-fade-enter-active, .zoom-fade-leave-active {
  transition: opacity 0.5s, transform 0.5s;
}

.zoom-fade-enter, .zoom-fade-leave-to {
  opacity: 0;
  transform: scale(1.2);
}

@keyframes zoomIn {
  0% { opacity: 0; transform: scale(0.5); }
  70% { opacity: 1; transform: scale(1.2); }
  100% { opacity: 1; transform: scale(1); }
}


/* 修改密码项样式，解决重叠问题 */
.password-item {
  position: relative;
  margin-bottom: 8px; /* 增加底部间距 */
}

.password-item .el-form-item__error {
  top: 100%; /* 错误提示放在底部 */
  margin-top: 4px; /* 增加上边距 */
}

/* 忘记密码链接样式调整 */
.forgot-password {
  display: block;
  text-align: right;
  color: rgba(255, 255, 255, 0.6) !important;
  text-decoration: none;
  font-size: 14px !important;
  margin-top: 8px; /* 增加上边距 */
  margin-bottom: 0;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
}

/* 验证码输入区域样式 */
.captcha-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 25px;
}

.input-group.captcha-group {
  flex: 1;
  margin-right: 10px;
}




/* 验证码容器样式 */
.captcha-container {
  display: flex;
  align-items: center;
  gap: 15px; /* 输入框和图片之间的间距 */
  width: 100%;
}

/* 验证码输入框样式 */
.captcha-input {
  flex: 1; /* 占据剩余空间 */
}

/* 验证码图片容器样式 */
.captcha-image-container {
  position: relative;
  width: 120px;
  height: 40px;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: transform 0.3s ease;
  flex-shrink: 0; /* 防止缩小 */
}

.captcha-image-container:hover {
  transform: translateY(-2px);
}

.captcha-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #f5f7fa;
}

/* 刷新图标样式 */
.refresh-icon {
  position: absolute;
  bottom: 5px;
  right: 5px;
  color: rgba(255, 255, 255, 0.8);
  background: rgba(0, 0, 0, 0.3);
  border-radius: 50%;
  padding: 3px;
  font-size: 14px;
  transition: all 0.3s ease;
  z-index: 2;
}

.refresh-icon:hover {
  color: #00c6ff;
  background: rgba(0, 0, 0, 0.5);
  transform: rotate(180deg);
}


/* 页脚样式 */
.footer {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 15px 0;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(5px);
  z-index: 10;
  text-align: center;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  line-height: 1.5;
}

.footer a {
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  transition: color 0.3s;
}

.footer a:hover {
  color: #00c6ff;
}

.divider {
  margin: 0 10px;
  color: rgba(255, 255, 255, 0.3);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .login-card {
    padding: 30px 20px;
  }
  
  .login-header h1 {
    font-size: 24px;
  }
  
  .logo {
    width: 70px;
    height: 70px;
  }
  
  .float-circle {
    display: none;
  }
    .captcha-item {
    flex-direction: column;
  }
  .captcha-container {
    flex-direction: column;
    gap: 10px;
  }
  
  .captcha-image-container {
    width: 100%;
  }
  
  .captcha-input .el-input__inner {
    padding-right: 20px;
  }

  .footer {
    padding: 10px 0;
  }
  
  .footer-content {
    font-size: 10px;
  }
  
  .divider {
    margin: 0 5px;
  }
}
</style>